/**
 * Phase 27.31 — Landing Page editor with live desktop + mobile preview.
 *
 * Handles both create (route `/leads/landing-pages/new`) and edit
 * (route `/leads/landing-pages/:id`). The public renderer
 * `PublicLandingPage` is reused inside an iframe-styled container
 * for the preview so what admins see is exactly what visitors see —
 * no drift between preview and production markup.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatPhone, formatPhoneInput } from '@/lib/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Rocket, Loader2, Monitor, Smartphone, Copy, ExternalLink, Upload, Image as ImageIcon,
} from 'lucide-react';
import { leadLandingPagesAPI, agentAPI as agentsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { SERVICE_CATALOG, TEMPLATE_STYLES, FIELD_VISIBILITY_OPTIONS, LANDING_MODELS, slugify } from '../lib/landingPageConstants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

// A miniature rendering of the public page — matches
// `PublicLandingPage` tokens closely enough to guide the admin
// without duplicating all of the target CSS.

// Palette tokens for the small 3x3 template thumbnails. Kept
// separate from `InlinePreview` because the thumbnail only needs
// three colors (hero gradient, accent, CTA) and a single label —
// duplicating the entire preview grammar here would make it
// visually noisy at this size.
const TEMPLATE_THUMB_TOKENS = {
  professional:     { hero: 'from-slate-900 via-slate-800 to-slate-900',    accent: '#7dd3fc', cta: 'bg-sky-500' },
  medicare:         { hero: 'from-blue-900 via-indigo-900 to-blue-900',      accent: '#fbbf24', cta: 'bg-amber-500' },
  family_health:    { hero: 'from-emerald-800 via-teal-800 to-cyan-800',     accent: '#6ee7b7', cta: 'bg-emerald-500' },
  modern_minimal:   { hero: 'from-neutral-900 via-zinc-900 to-black',        accent: '#fb7185', cta: 'bg-rose-500' },
  elegant_dark:     { hero: 'from-stone-900 via-zinc-950 to-stone-900',      accent: '#fbbf24', cta: 'bg-amber-500' },
  warm_latino:      { hero: 'from-orange-900 via-red-900 to-rose-900',       accent: '#fdba74', cta: 'bg-orange-500' },
  wellness_bright:  { hero: 'from-rose-800 via-pink-800 to-orange-800',      accent: '#fde047', cta: 'bg-yellow-400' },
  ocean_blue:       { hero: 'from-cyan-900 via-sky-900 to-blue-900',         accent: '#67e8f9', cta: 'bg-cyan-500' },
  royal_purple:     { hero: 'from-violet-950 via-purple-900 to-indigo-950',  accent: '#c4b5fd', cta: 'bg-violet-500' },
  crimson_red:      { hero: 'from-red-950 via-rose-900 to-red-950',          accent: '#fda4af', cta: 'bg-red-500' },
  forest_green:     { hero: 'from-green-950 via-emerald-900 to-green-950',   accent: '#6ee7b7', cta: 'bg-green-600' },
  sunset_coral:     { hero: 'from-rose-900 via-orange-800 to-amber-800',     accent: '#fcd34d', cta: 'bg-rose-500' },
  midnight_gold:    { hero: 'from-slate-950 via-blue-950 to-slate-950',      accent: '#fcd34d', cta: 'bg-amber-400' },
};

function TemplateThumbnail({ style, active, onSelect }) {
  const t = TEMPLATE_THUMB_TOKENS[style.key] || TEMPLATE_THUMB_TOKENS.professional;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left rounded-xl overflow-hidden border-2 transition group ${
        active ? 'border-slate-900 ring-2 ring-slate-900/20' : 'border-slate-200 hover:border-slate-400'
      }`}
      data-testid={`editor-tpl-${style.key}`}
    >
      {/* Hero preview — gradient background, mock headline + tagline
          in the accent color, and a small CTA pill. */}
      <div className={`h-24 sm:h-28 bg-gradient-to-br ${t.hero} px-3 py-2.5 flex flex-col justify-between text-white`}>
        <div>
          <div className="h-1.5 w-16 bg-white/80 rounded" />
          <div className="h-1 w-10 bg-white/40 rounded mt-1.5" />
        </div>
        <div className="flex items-end justify-between gap-1">
          <div className="text-[10px] italic leading-none truncate" style={{ color: t.accent }}>
            Your Protection
          </div>
          <div className={`${t.cta} h-3 w-8 rounded-full flex-shrink-0`} />
        </div>
      </div>
      {/* Label strip */}
      <div className="px-2.5 py-1.5 bg-white flex items-center justify-between gap-1">
        <div className="text-[11px] sm:text-xs font-semibold text-slate-900 truncate">{style.label}</div>
        {active && (
          <span className="w-4 h-4 rounded-full bg-slate-900 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">
            ✓
          </span>
        )}
      </div>
    </button>
  );
}

function InlinePreview({ page, agentProfilePhotoUrl }) {
  const tpl = page.template_style || 'professional';
  const bgs = {
    professional: 'from-slate-900 via-slate-800 to-slate-900',
    medicare: 'from-blue-900 via-indigo-900 to-blue-900',
    family_health: 'from-emerald-800 via-teal-800 to-cyan-800',
    modern_minimal: 'from-neutral-900 via-zinc-900 to-black',
    elegant_dark: 'from-stone-900 via-zinc-950 to-stone-900',
    warm_latino: 'from-orange-900 via-red-900 to-rose-900',
    wellness_bright: 'from-rose-800 via-pink-800 to-orange-800',
    ocean_blue: 'from-cyan-900 via-sky-900 to-blue-900',
    royal_purple: 'from-violet-950 via-purple-900 to-indigo-950',
    crimson_red: 'from-red-950 via-rose-900 to-red-950',
    forest_green: 'from-green-950 via-emerald-900 to-green-950',
    sunset_coral: 'from-rose-900 via-orange-800 to-amber-800',
    midnight_gold: 'from-slate-950 via-blue-950 to-slate-950',
  };
  const accents = {
    professional: 'text-sky-400',
    medicare: 'text-amber-300',
    family_health: 'text-emerald-300',
    modern_minimal: 'text-rose-400',
    elegant_dark: 'text-amber-400',
    warm_latino: 'text-orange-300',
    wellness_bright: 'text-yellow-300',
    ocean_blue: 'text-cyan-300',
    royal_purple: 'text-violet-300',
    crimson_red: 'text-rose-300',
    forest_green: 'text-emerald-300',
    sunset_coral: 'text-amber-200',
    midnight_gold: 'text-amber-300',
  };
  // Hex colors used only by the cursive-tagline color prop below, so
  // it inherits the accent hue when React renders the preview.
  const accentHex = {
    professional: '#7dd3fc',
    medicare: '#fbbf24',
    family_health: '#6ee7b7',
    modern_minimal: '#fb7185',
    elegant_dark: '#fbbf24',
    warm_latino: '#fdba74',
    wellness_bright: '#fde047',
    ocean_blue: '#67e8f9',
    royal_purple: '#c4b5fd',
    crimson_red: '#fda4af',
    forest_green: '#6ee7b7',
    sunset_coral: '#fcd34d',
    midnight_gold: '#fcd34d',
  };
  const chips = {
    professional: 'bg-sky-50 text-sky-800 border-sky-200',
    medicare: 'bg-amber-100 text-amber-900 border-amber-300',
    family_health: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    modern_minimal: 'bg-rose-50 text-rose-800 border-rose-200',
    elegant_dark: 'bg-amber-50 text-amber-900 border-amber-200',
    warm_latino: 'bg-orange-50 text-orange-800 border-orange-200',
    wellness_bright: 'bg-yellow-50 text-yellow-900 border-yellow-200',
    ocean_blue: 'bg-cyan-50 text-cyan-800 border-cyan-200',
    royal_purple: 'bg-violet-50 text-violet-800 border-violet-200',
    crimson_red: 'bg-rose-50 text-rose-800 border-rose-200',
    forest_green: 'bg-green-50 text-green-800 border-green-200',
    sunset_coral: 'bg-orange-50 text-orange-800 border-orange-200',
    midnight_gold: 'bg-amber-50 text-amber-900 border-amber-200',
  };
  const btn = {
    professional: 'bg-sky-600 text-white',
    medicare: 'bg-amber-500 text-slate-900',
    family_health: 'bg-emerald-600 text-white',
    modern_minimal: 'bg-rose-500 text-white',
    elegant_dark: 'bg-amber-500 text-stone-900',
    warm_latino: 'bg-orange-500 text-white',
    wellness_bright: 'bg-yellow-400 text-slate-900',
    ocean_blue: 'bg-cyan-600 text-white',
    royal_purple: 'bg-violet-600 text-white',
    crimson_red: 'bg-red-600 text-white',
    forest_green: 'bg-green-600 text-white',
    sunset_coral: 'bg-rose-500 text-white',
    midnight_gold: 'bg-amber-400 text-slate-900',
  };
  const heroSize = tpl === 'medicare' ? 'text-2xl' : 'text-xl';
  const bodyText = tpl === 'medicare' ? 'text-sm' : 'text-xs';

  const enabled = SERVICE_CATALOG.filter((s) => (page.services_enabled || []).includes(s.key));
  const mode = page.agent_display?.photo_mode || 'profile';
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  // Marketing photo may be either a persisted server URL (relative
  // `/api/...`) OR a local `blob:` preview from the pending-upload
  // flow. Blob URLs are used as-is; server URLs get the backend
  // prefix + a cache-buster.
  const rawMarketing = page.agent_display?.marketing_photo_url;
  const photoUrl = mode === 'none'
    ? null
    : mode === 'custom'
      ? (rawMarketing
          ? (rawMarketing.startsWith('blob:') || rawMarketing.startsWith('http')
              ? rawMarketing
              : `${backend}${rawMarketing}?v=${Date.now()}`)
          : null)
      : (agentProfilePhotoUrl ? (agentProfilePhotoUrl.startsWith('http') ? agentProfilePhotoUrl : `${backend}${agentProfilePhotoUrl}`) : null);
  const initials = (page.agent_display?.display_name || '').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200">
      <div className={`bg-gradient-to-br ${bgs[tpl]} text-white p-4 flex flex-col items-center text-center gap-2`}>
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white/30" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center font-bold">
            {initials || 'A'}
          </div>
        )}
        {page.agency_brand?.show && (
          <div className={`text-[10px] uppercase tracking-widest ${accents[tpl]}`}>{page.agency_brand.name}</div>
        )}
        <div className="font-semibold">{page.agent_display?.display_name || '—'}</div>
        <div className={`opacity-80 ${bodyText}`}>{page.agent_display?.professional_title}</div>
        <div className={`font-black leading-tight mt-1 ${heroSize}`}>{page.content?.headline || '—'}</div>
        <div className={`opacity-90 ${bodyText}`}>{page.content?.supporting_text}</div>
        {page.content?.show_highlight && page.content?.highlight_message && (
          <span
            className={
              page.content?.highlight_cursive
                ? 'mt-1 font-cursive-tagline italic text-lg'
                : `text-[10px] px-2 py-0.5 rounded-full border ${chips[tpl]}`
            }
            style={page.content?.highlight_cursive ? {
              fontFamily: '"Great Vibes","Dancing Script","Brush Script MT",cursive',
              color: accentHex[tpl] || '#7dd3fc',
            } : undefined}
          >
            {page.content.highlight_message}
          </span>
        )}
      </div>
      <div className="bg-white p-4 space-y-2">
        {enabled.length > 1 && (
          <div className="flex flex-wrap gap-1">
            {enabled.map((s) => (
              <span key={s.key} className="text-[10px] px-2 py-0.5 border rounded bg-slate-50 text-slate-700">{s.label}</span>
            ))}
          </div>
        )}
        <div className="h-6 bg-slate-100 rounded" />
        <div className="h-6 bg-slate-100 rounded" />
        <div className="h-6 bg-slate-100 rounded" />
        <div className={`rounded text-center py-2 text-xs font-semibold ${btn[tpl]}`}>Request Information</div>
      </div>
    </div>
  );
}

const EMPTY = {
  name: '', slug: '', assigned_agent_npn: '', template_style: 'professional',
  layout: 'standard',
  services_enabled: ['medicare'],
  agent_display: {
    display_name: '', professional_title: 'Licensed Insurance Agent',
    phone: '', email: '', photo_mode: 'profile', marketing_photo_url: null,
  },
  agency_brand: { show: true, name: 'KR Enterprise Group', powered_by_kr: true },
  content: {
    headline: "Let's Find the Coverage That Fits Your Needs",
    supporting_text: "Have questions about Medicare, health insurance, life insurance or other coverage? Complete the form and I'll contact you personally.",
    highlight_message: 'Personalized Insurance Assistance',
    show_highlight: true,
    // Renders the highlight in a script font (Great Vibes) — matches
    // the mock's cursive tagline effect. Font loaded on-demand.
    highlight_cursive: false,
    cta_text: '',
    // Phase 27.41 — Bilingual hero overrides. Optional; empty falls back
    // to the primary (English) copy on the public page.
    headline_es: '',
    supporting_text_es: '',
    highlight_message_es: '',
    cta_text_es: '',
  },
  form_config: { zip: 'optional', email: 'optional', preferred_contact: 'optional', message: 'optional' },
  appointments_config: {
    enabled: false,
    duration_minutes: 30,
    timezone: 'America/New_York',
    available_windows: [
      // Weekdays 9-5 by default — admin can trim/extend after enabling.
      { dow: 0, start_time: '09:00', end_time: '17:00' },
      { dow: 1, start_time: '09:00', end_time: '17:00' },
      { dow: 2, start_time: '09:00', end_time: '17:00' },
      { dow: 3, start_time: '09:00', end_time: '17:00' },
      { dow: 4, start_time: '09:00', end_time: '17:00' },
    ],
    min_notice_hours: 4,
    max_days_ahead: 30,
    buffer_minutes: 0,
  },
  status: 'draft',
};

export default function LeadLandingPageEditor({ mine = false }) {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();
  const basePath = mine ? '/my-landing-page' : '/leads/landing-pages';
  const myNpn = user?.agent_npn || user?.active_context?.id || '';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(EMPTY);
  const [agents, setAgents] = useState([]);
  const [previewMode, setPreviewMode] = useState('mobile');
  const [wizardStep, setWizardStep] = useState(0);
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'ok' | 'taken' | 'invalid'
  const [selectedAgentPhoto, setSelectedAgentPhoto] = useState(null);
  const [tab, setTab] = useState('overview');
  // Snapshot of the assigned agent at page load — used to detect
  // "the admin just changed the owner" so we can prompt them about
  // reassigning historical leads.
  const [initialAgentNpn, setInitialAgentNpn] = useState('');
  const [leadsList, setLeadsList] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [activityList, setActivityList] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const slugRef = useRef();
  const fileInputRef = useRef();
  const logoInputRef = useRef();
  // Held marketing-photo file that the admin picked before the
  // landing page has an id yet. Uploaded after create.
  const [pendingPhoto, setPendingPhoto] = useState(null);
  // Same pattern for the optional agency-brand logo upload.
  const [pendingLogo, setPendingLogo] = useState(null);

  // Load Google's "Great Vibes" font once so the inline preview
  // renders the cursive tagline exactly like the public page. Only
  // fetched when the admin actually enables the cursive toggle.
  useEffect(() => {
    if (!page.content?.highlight_cursive) return;
    const id = 'lp-editor-cursive-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(link);
  }, [page.content?.highlight_cursive]);

  // Bootstrap: load agents list + existing page (if editing).
  useEffect(() => {
    (async () => {
      try {
        const [agentsR, pageR] = await Promise.all([
          // Agents can't list all agents — self-service editor doesn't
          // need it (the owner is always the logged-in agent).
          mine ? Promise.resolve({ data: [] }) : agentsAPI.list({ limit: 500 }),
          !isNew ? leadLandingPagesAPI.get(id) : Promise.resolve(null),
        ]);
        const agentsList = Array.isArray(agentsR.data) ? agentsR.data : (agentsR.data?.items || []);
        setAgents(agentsList);
        if (pageR) {
          setPage({
            ...EMPTY, ...pageR.data,
            agent_display: { ...EMPTY.agent_display, ...(pageR.data.agent_display || {}) },
            content: { ...EMPTY.content, ...(pageR.data.content || {}) },
            appointments_config: { ...EMPTY.appointments_config, ...(pageR.data.appointments_config || {}) },
          });
          setInitialAgentNpn(pageR.data.assigned_agent_npn || '');
          const a = agentsList.find((x) => x.npn === pageR.data.assigned_agent_npn);
          setSelectedAgentPhoto(a?.photo_url || null);
        } else if (isNew && mine && myNpn) {
          // Self-service new page — owner is always the current agent.
          setPage((p) => ({ ...p, assigned_agent_npn: myNpn }));
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, [id, isNew]);

  // Auto-slug from the name field on new pages only.
  useEffect(() => {
    if (!isNew) return;
    if (slugRef.current && slugRef.current !== page.slug) return; // user typed
    const s = slugify(page.name);
    setPage((p) => ({ ...p, slug: s }));
  }, [page.name, isNew]);

  // Debounced slug availability check.
  useEffect(() => {
    if (!page.slug || page.slug.length < 3) { setSlugStatus(null); return; }
    setSlugStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await leadLandingPagesAPI.checkSlug(page.slug);
        setSlugStatus(r.data.available ? 'ok' : (r.data.reason ? 'invalid' : 'taken'));
      } catch { setSlugStatus('invalid'); }
    }, 300);
    return () => clearTimeout(t);
  }, [page.slug]);

  const selectedAgent = useMemo(
    () => agents.find((a) => a.npn === page.assigned_agent_npn),
    [agents, page.assigned_agent_npn],
  );

  // When agent changes on a NEW page, prefill display defaults.
  const onAgentChange = useCallback((npn) => {
    const a = agents.find((x) => x.npn === npn);
    setSelectedAgentPhoto(a?.photo_url || null);
    setPage((p) => {
      const next = { ...p, assigned_agent_npn: npn };
      if (isNew) {
        next.agent_display = {
          ...p.agent_display,
          display_name: a ? [a.first_name, a.last_name].filter(Boolean).join(' ') : '',
          phone: a?.phone || '',
          email: a?.email || '',
        };
      }
      return next;
    });
  }, [agents, isNew]);

  const toggleService = (key) => {
    setPage((p) => ({
      ...p,
      services_enabled: p.services_enabled.includes(key)
        ? p.services_enabled.filter((s) => s !== key)
        : [...p.services_enabled, key],
    }));
  };

  const uploadMarketingPhoto = async (file) => {
    if (isNew) {
      // Hold the file in memory until the landing page has an id.
      // We show a local preview so the admin can see what they
      // picked, and auto-upload the moment `persist()` creates the
      // page (see the create branch below).
      setPendingPhoto(file);
      const previewUrl = URL.createObjectURL(file);
      setPage((p) => ({
        ...p,
        agent_display: {
          ...p.agent_display,
          photo_mode: 'custom',
          // Absolute blob URL — `absoluteUrl()` in the preview
          // recognises the "http..." prefix and passes it through.
          marketing_photo_url: previewUrl,
        },
      }));
      toast.success('Photo ready — it will upload on save');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await leadLandingPagesAPI.uploadPhoto(id, fd);
      setPage((p) => ({
        ...p,
        agent_display: {
          ...p.agent_display,
          photo_mode: 'custom',
          marketing_photo_url: r.data.marketing_photo_url,
        },
      }));
      toast.success('Marketing photo uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const uploadAgencyLogo = async (file) => {
    // Mirror `uploadMarketingPhoto`: if we don't have a landing page
    // id yet, hold the file locally and show a blob preview until
    // persist() can auto-upload it after create.
    if (isNew) {
      setPendingLogo(file);
      const previewUrl = URL.createObjectURL(file);
      setPage((p) => ({
        ...p,
        agency_brand: { ...p.agency_brand, logo_url: previewUrl },
      }));
      toast.success('Logo ready — it will upload on save');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await leadLandingPagesAPI.uploadAgencyLogo(id, fd);
      setPage((p) => ({
        ...p,
        agency_brand: { ...p.agency_brand, logo_url: r.data.logo_url },
      }));
      toast.success('Agency logo uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const removeAgencyLogo = async () => {
    if (isNew || !page.agency_brand?.logo_url) {
      // Nothing persisted yet — just clear the local pending state.
      setPendingLogo(null);
      setPage((p) => ({ ...p, agency_brand: { ...p.agency_brand, logo_url: null } }));
      return;
    }
    try {
      await leadLandingPagesAPI.removeAgencyLogo(id);
      setPage((p) => ({ ...p, agency_brand: { ...p.agency_brand, logo_url: null } }));
      toast.success('Agency logo removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  const persist = async (extraFields = {}) => {
    setSaving(true);
    try {
      const isTeamLayout = (page.layout === 'team_appointments');
      const payload = {
        name: page.name, slug: page.slug,
        assigned_agent_npn: page.assigned_agent_npn || null,
        template_style: page.template_style,
        layout: page.layout || 'standard',
        services_enabled: page.services_enabled,
        agent_display: page.agent_display,
        agency_brand: page.agency_brand,
        content: page.content,
        form_config: page.form_config,
        appointments_config: page.appointments_config,
        team_roster: isTeamLayout ? (page.team_roster || []) : undefined,
        ...extraFields,
      };
      if (isNew) {
        if (!payload.name?.trim()) { toast.error('Name is required'); return null; }
        if (!isTeamLayout && !payload.assigned_agent_npn) { toast.error('Assign an agent'); return null; }
        if (isTeamLayout && !(page.team_roster || []).length) { toast.error('Add at least one agent to the team roster'); return null; }
        if (!payload.slug) { toast.error('Slug is required'); return null; }
        // Clear the local blob preview URL we set on the pending
        // photo so the persisted landing doesn't ship it — the
        // real URL comes from the upload response below.
        if (pendingPhoto) {
          payload.agent_display = {
            ...payload.agent_display, marketing_photo_url: null,
          };
        }
        if (pendingLogo) {
          payload.agency_brand = {
            ...payload.agency_brand, logo_url: null,
          };
        }
        const r = await (mine ? leadLandingPagesAPI.createMine(payload) : leadLandingPagesAPI.create(payload));
        // Auto-upload the marketing photo the admin queued while
        // creating the page — this makes the first-save flow
        // single-shot instead of two-step.
        if (pendingPhoto) {
          try {
            const fd = new FormData();
            fd.append('file', pendingPhoto);
            const up = await leadLandingPagesAPI.uploadPhoto(r.data.landing_page_id, fd);
            r.data.agent_display = {
              ...r.data.agent_display,
              photo_mode: 'custom',
              marketing_photo_url: up.data.marketing_photo_url,
            };
            setPendingPhoto(null);
          } catch (e) {
            toast.error('Landing page saved, but the photo upload failed. Please retry.');
          }
        }
        if (pendingLogo) {
          try {
            const fd = new FormData();
            fd.append('file', pendingLogo);
            const up = await leadLandingPagesAPI.uploadAgencyLogo(r.data.landing_page_id, fd);
            r.data.agency_brand = {
              ...r.data.agency_brand,
              logo_url: up.data.logo_url,
            };
            setPendingLogo(null);
          } catch (e) {
            toast.error('Landing page saved, but the logo upload failed. Please retry.');
          }
        }
        toast.success('Landing page created');
        navigate(`${basePath}/${r.data.landing_page_id}`, { replace: true });
        return r.data;
      }
      const r = await leadLandingPagesAPI.update(id, payload);
      toast.success('Saved');
      setPage((p) => ({ ...p, ...r.data }));
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      return null;
    } finally { setSaving(false); }
  };

  const publish = async () => {
    const r = await persist({ status: 'active' });
    if (r) toast.success('Published — public page is live');
  };

  const copyLink = () => {
    const url = `${window.location.origin}/connect/${page.slug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link copied'),
      () => toast.error('Could not copy'),
    );
  };

  // Load Leads / Activity lazily when their tabs open — no reason to
  // fetch either until the admin actually asks for them.
  useEffect(() => {
    if (isNew) return;
    if (tab === 'leads' && leadsList === null && !leadsLoading) {
      setLeadsLoading(true);
      leadLandingPagesAPI.leads(id)
        .then((r) => setLeadsList(r.data || []))
        .catch(() => setLeadsList([]))
        .finally(() => setLeadsLoading(false));
    }
    if (tab === 'activity' && activityList === null && !activityLoading) {
      setActivityLoading(true);
      leadLandingPagesAPI.activity(id)
        .then((r) => setActivityList(r.data || []))
        .catch(() => setActivityList([]))
        .finally(() => setActivityLoading(false));
    }
    if (tab === 'analytics' && analytics === null && !analyticsLoading) {
      setAnalyticsLoading(true);
      leadLandingPagesAPI.analytics(id, 30)
        .then((r) => setAnalytics(r.data))
        .catch(() => setAnalytics({ totals: { views: 0, leads: 0 }, series: [] }))
        .finally(() => setAnalyticsLoading(false));
    }
  }, [tab, id, isNew, leadsList, leadsLoading, activityList, activityLoading, analytics, analyticsLoading]);

  const agentChanged = !isNew && initialAgentNpn && initialAgentNpn !== page.assigned_agent_npn;

  const reassignHistorical = async () => {
    const targetName = selectedAgent
      ? [selectedAgent.first_name, selectedAgent.last_name].filter(Boolean).join(' ')
      : page.assigned_agent_npn;
    if (!window.confirm(
      `Reassign all previously-generated leads on this landing page to ${targetName}? `
      + 'Leads that were manually reassigned by someone else will only be moved if they still belong to the previous landing-page owner.',
    )) return;
    setReassigning(true);
    try {
      const r = await leadLandingPagesAPI.reassignHistorical(id, {
        old_agent_npn: initialAgentNpn,
        new_agent_npn: page.assigned_agent_npn,
      });
      toast.success(`Reassigned ${r.data.reassigned} lead${r.data.reassigned === 1 ? '' : 's'} to ${targetName}`);
      setInitialAgentNpn(page.assigned_agent_npn);
      // Invalidate cached tabs so they refetch on next open.
      setLeadsList(null);
      setActivityList(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassignment failed');
    } finally { setReassigning(false); }
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }

  const slugMsg = slugStatus === 'checking' ? 'Checking…'
    : slugStatus === 'ok' ? 'Available'
    : slugStatus === 'taken' ? 'Already in use'
    : slugStatus === 'invalid' ? 'Invalid format'
    : null;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="landing-editor">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(basePath)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? (mine ? 'Create My Landing Page' : 'Create Landing Page') : page.name || 'Untitled'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && page.slug && (
            <>
              <Button variant="outline" onClick={copyLink} data-testid="editor-copy-link">
                <Copy className="h-4 w-4 mr-1" /> Copy link
              </Button>
              {page.status === 'active' && (
                <a href={`/connect/${page.slug}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" data-testid="editor-open-public">
                    <ExternalLink className="h-4 w-4 mr-1" /> Open
                  </Button>
                </a>
              )}
            </>
          )}
          <Button variant="outline" onClick={() => persist()} disabled={saving} data-testid="editor-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {isNew ? 'Save draft' : 'Save'}
          </Button>
          {!isNew && page.status !== 'active' && (
            <Button
              onClick={publish} disabled={saving || slugStatus === 'taken' || slugStatus === 'invalid'}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="editor-publish"
            >
              <Rocket className="h-4 w-4 mr-1" /> Publish
            </Button>
          )}
        </div>
      </div>

      {/* When editing an existing page, expose Overview / Leads /
          Activity tabs. New pages show only the editor (no leads or
          activity exist yet). */}
      {isNew ? (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {overviewLeftColumn()}
          {previewColumn()}
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} data-testid="editor-tabs">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">
              Leads{leadsList !== null ? ` · ${leadsList.length}` : ''}
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {overviewLeftColumn()}
              {previewColumn()}
            </div>
          </TabsContent>
          <TabsContent value="leads">
            <LeadsTab loading={leadsLoading} leads={leadsList} />
          </TabsContent>
          <TabsContent value="activity">
            <ActivityTab loading={activityLoading} rows={activityList} />
          </TabsContent>
          <TabsContent value="analytics">
            <AnalyticsTab loading={analyticsLoading} data={analytics} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );

  // ── inline sub-renderers ────────────────────────────────────────
  // Kept as plain function calls (not JSX components) so React
  // preserves inner input focus across renders — creating a
  // component-per-render would remount the whole subtree on every
  // keystroke.

  function overviewLeftColumn() {
    const L = page.layout || 'standard';
    const stepDefs = [
      { key: 'model', label: 'Model' },
      { key: 'style', label: 'Style' },
      { key: 'basics', label: 'Basics' },
      { key: 'agent', label: L === 'team_appointments' ? 'Branding' : 'Agent & Brand' },
      { key: 'content', label: 'Content' },
      ...(['standard', 'lead_capture', 'single_product', 'event'].includes(L) ? [{ key: 'services', label: 'Services' }] : []),
      ...(!['digital_card', 'appointments_only', 'team_appointments'].includes(L) ? [{ key: 'form', label: 'Form' }] : []),
      ...(!['lead_capture', 'simple', 'digital_card'].includes(L) ? [{ key: 'appointments', label: 'Booking' }] : []),
      { key: 'review', label: 'Review' },
    ];
    const sIdx = Math.min(wizardStep, stepDefs.length - 1);
    const sKey = stepDefs[sIdx]?.key;
    const go = (n) => setWizardStep(Math.max(0, Math.min(n, stepDefs.length - 1)));
    return (
      <div className="xl:col-span-2 space-y-4" key="overview-left">
          {/* Step-by-step progress. Each pill is clickable to jump. */}
          <div className="flex items-center gap-1.5 flex-wrap bg-white rounded-2xl border border-slate-200 shadow-sm p-3" data-testid="wizard-progress">
            {stepDefs.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => go(i)}
                data-testid={`wizard-step-${s.key}`}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border transition-colors ${
                  i === sIdx
                    ? 'bg-slate-900 text-white border-slate-900'
                    : i < sIdx
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center ${
                  i === sIdx ? 'bg-white text-slate-900' : i < sIdx ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                }`}>{i < sIdx ? '✓' : i + 1}</span>
                {s.label}
              </button>
            ))}
          </div>

          {sKey === 'model' && (
          <Card title="Choose a model">
            <p className="text-xs text-slate-500 -mt-2 mb-2">Pick the type of page. The next steps adapt to your choice.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="editor-model-grid">
              {LANDING_MODELS.map((m) => {
                const active = (page.layout || 'standard') === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setPage({ ...page, layout: m.key })}
                    data-testid={`editor-model-${m.key}`}
                    className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${
                      active ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-800">{m.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
                  </button>
                );
              })}
            </div>
            {page.layout === 'appointments_only' && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3" data-testid="editor-appt-only-hint">
                This page shows only the booking widget. Enable appointments and add availability in the Booking step.
              </p>
            )}
          </Card>
          )}

          {sKey === 'style' && (
          <Card title="Choose a style &amp; colors">
            <Field label="Template style">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="editor-template-grid">
                {TEMPLATE_STYLES.map((t) => (
                  <TemplateThumbnail
                    key={t.key}
                    style={t}
                    active={page.template_style === t.key}
                    onSelect={() => setPage({ ...page, template_style: t.key })}
                  />
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {TEMPLATE_STYLES.find((t) => t.key === page.template_style)?.description}
              </p>
            </Field>
          </Card>
          )}

          {sKey === 'basics' && (
          <Card title="Basics">
            <Field label="Landing page name">
              <Input value={page.name} onChange={(e) => setPage({ ...page, name: e.target.value })}
                placeholder="Kevin Rodriguez — Medicare Events"
                data-testid="editor-name" />
            </Field>
            {L !== 'team_appointments' && (
            <Field label="Assigned agent">
              {mine ? (
                <Input value={page.agent_display?.display_name || myNpn} disabled data-testid="editor-agent-self" />
              ) : (
              <Select value={page.assigned_agent_npn} onValueChange={onAgentChange}>
                <SelectTrigger data-testid="editor-agent"><SelectValue placeholder="Select an agent…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {agents.map((a) => (
                    <SelectItem key={a.npn} value={a.npn}>
                      {[a.first_name, a.last_name].filter(Boolean).join(' ')} · NPN {a.npn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
            </Field>
            )}
            <Field label="Public URL slug"
              hint={<span className={slugStatus === 'ok' ? 'text-emerald-600' : (slugStatus && slugStatus !== 'checking' ? 'text-rose-600' : 'text-slate-500')}>
                /connect/<b>{page.slug || '…'}</b> {slugMsg && <>· {slugMsg}</>}
              </span>}>
              <Input value={page.slug}
                onChange={(e) => { slugRef.current = e.target.value; setPage({ ...page, slug: slugify(e.target.value) }); }}
                placeholder="kevin-medicare"
                data-testid="editor-slug" />
            </Field>
            {L === 'team_appointments' && (
              <Field label="Team roster"
                hint="Add the agents visitors can book with. Per agent you can override the photo, set contact info, turn 'Request information' and 'Book appointment' on/off, and set their own availability — all here, according to what this page needs.">
                <TeamRosterEditor
                  agents={agents}
                  roster={page.team_roster || []}
                  onChange={(roster) => setPage({ ...page, team_roster: roster })}
                  pageId={id}
                  isNew={isNew}
                />
              </Field>
            )}
          </Card>
          )}

          {sKey === 'agent' && (
          <Card title="Agent Information (public-facing)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Display name">
                <Input value={page.agent_display.display_name}
                  onChange={(e) => setPage({ ...page, agent_display: { ...page.agent_display, display_name: e.target.value } })}
                  data-testid="editor-display-name" />
              </Field>
              <Field label="Professional title">
                <Input value={page.agent_display.professional_title}
                  onChange={(e) => setPage({ ...page, agent_display: { ...page.agent_display, professional_title: e.target.value } })}
                  data-testid="editor-title" />
              </Field>
              <Field label="Phone">
                <Input value={formatPhoneInput(page.agent_display.phone || '')}
                  onChange={(e) => setPage({ ...page, agent_display: { ...page.agent_display, phone: e.target.value } })} />
              </Field>
              <Field label="Email">
                <Input type="email" value={page.agent_display.email || ''}
                  onChange={(e) => setPage({ ...page, agent_display: { ...page.agent_display, email: e.target.value } })} />
              </Field>
            </div>
            <Field label="Photo">
              <div className="flex items-center gap-2 flex-wrap">
                {['profile', 'custom', 'none'].map((m) => (
                  <button key={m} type="button"
                    onClick={() => setPage({ ...page, agent_display: { ...page.agent_display, photo_mode: m } })}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${page.agent_display.photo_mode === m ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 hover:border-slate-500'}`}
                    data-testid={`editor-photo-${m}`}>
                    {m === 'profile' ? 'Use profile photo' : m === 'custom' ? 'Upload marketing photo' : 'No photo'}
                  </button>
                ))}
                {page.agent_display.photo_mode === 'custom' && (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => { if (e.target.files?.[0]) uploadMarketingPhoto(e.target.files[0]); }} />
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-3 w-3 mr-1" /> {page.agent_display.marketing_photo_url ? 'Replace' : 'Upload'}
                    </Button>
                    {isNew && pendingPhoto && (
                      <span className="text-xs text-emerald-600">Ready — will upload on save</span>
                    )}
                  </>
                )}
              </div>
              {page.agent_display.photo_mode === 'profile' && (
                <div className="mt-2 flex items-center gap-3" data-testid="editor-profile-photo-preview">
                  {selectedAgentPhoto ? (
                    <img
                      src={selectedAgentPhoto.startsWith('http') ? selectedAgentPhoto : `${process.env.REACT_APP_BACKEND_URL || ''}${selectedAgentPhoto}`}
                      alt=""
                      className="w-14 h-14 rounded-full object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 text-[10px] text-center leading-tight px-1">No profile photo</div>
                  )}
                  <p className="text-xs text-slate-500 max-w-xs">
                    {selectedAgentPhoto
                      ? "Using this agent's profile photo. Pick \u201cUpload marketing photo\u201d to use a different image just for this page."
                      : "This agent has no profile photo yet. Upload a marketing photo here, or add one on the agent's profile."}
                  </p>
                </div>
              )}
            </Field>
            <Field label="Agency branding">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={page.agency_brand.show}
                  onChange={(e) => setPage({ ...page, agency_brand: { ...page.agency_brand, show: e.target.checked } })} />
                Show agency brand
              </label>
              {page.agency_brand.show && (
                <Input className="mt-2" value={page.agency_brand.name}
                  onChange={(e) => setPage({ ...page, agency_brand: { ...page.agency_brand, name: e.target.value } })} />
              )}
              {page.agency_brand.show && (
                <div className="mt-3" data-testid="editor-agency-logo">
                  <div className="text-xs font-semibold text-slate-600 mb-1.5">Agency logo (1:1 square, PNG/JPG, up to 4MB)</div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded border border-slate-200 bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
                      {page.agency_brand.logo_url ? (
                        <img
                          src={
                            page.agency_brand.logo_url.startsWith('blob:') || page.agency_brand.logo_url.startsWith('http')
                              ? page.agency_brand.logo_url
                              : `${process.env.REACT_APP_BACKEND_URL || ''}${page.agency_brand.logo_url}?v=${Date.now()}`
                          }
                          alt="Agency logo"
                          className="w-full h-full object-contain"
                          data-testid="editor-agency-logo-preview"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-900 text-white flex items-center justify-center font-black text-xs">KR</div>
                      )}
                    </div>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="editor-agency-logo-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadAgencyLogo(f);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      data-testid="editor-agency-logo-upload">
                      <Upload className="h-3 w-3 mr-1" />
                      {page.agency_brand.logo_url ? 'Replace' : 'Upload'}
                    </Button>
                    {page.agency_brand.logo_url && (
                      <Button
                        type="button" variant="ghost" size="sm"
                        onClick={removeAgencyLogo}
                        data-testid="editor-agency-logo-remove"
                        className="text-red-600 hover:text-red-700">
                        Remove
                      </Button>
                    )}
                  </div>
                  {isNew && pendingLogo && (
                    <p className="text-xs text-amber-600 mt-1">Will upload on save</p>
                  )}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm mt-3" data-testid="editor-powered-by-kr">
                <input type="checkbox" checked={!!page.agency_brand.powered_by_kr}
                  onChange={(e) => setPage({ ...page, agency_brand: { ...page.agency_brand, powered_by_kr: e.target.checked } })} />
                Show &quot;Powered by KR Enterprise Group&quot; in footer
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Available regardless of the agency name above — leave on when the agent works through KR even if their public brand differs.
              </p>
            </Field>
          </Card>
          )}

          {sKey === 'content' && (<>
          <Card title="Page Content">
            <Field label="Main headline">
              <Input value={page.content.headline}
                onChange={(e) => setPage({ ...page, content: { ...page.content, headline: e.target.value } })}
                data-testid="editor-headline" />
            </Field>
            <Field label="Supporting text">
              <Textarea rows={3} value={page.content.supporting_text}
                onChange={(e) => setPage({ ...page, content: { ...page.content, supporting_text: e.target.value } })} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={page.content.show_highlight}
                onChange={(e) => setPage({ ...page, content: { ...page.content, show_highlight: e.target.checked } })} />
              Show highlight badge
            </label>
            {page.content.show_highlight && (
              <>
                <Input value={page.content.highlight_message || ''}
                  onChange={(e) => setPage({ ...page, content: { ...page.content, highlight_message: e.target.value } })}
                  placeholder="Personalized Insurance Assistance" />
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={!!page.content.highlight_cursive}
                    onChange={(e) => setPage({ ...page, content: { ...page.content, highlight_cursive: e.target.checked } })}
                    data-testid="editor-highlight-cursive" />
                  Use cursive script font (like &ldquo;Your Protection is My Priority&rdquo; on the mock)
                </label>
              </>
            )}
            <Field label="Submit button label (optional)">
              <Input value={page.content.cta_text || ''}
                onChange={(e) => setPage({ ...page, content: { ...page.content, cta_text: e.target.value } })}
                placeholder="Request Information"
                data-testid="editor-cta-text" />
              <p className="text-[11px] text-slate-500 mt-1">
                Leave empty to use the default (&ldquo;Request Information&rdquo;).
              </p>
            </Field>
          </Card>

          {/* Phase 27.41 — Spanish translation card. Everything is
              optional; blank fields fall back to the English copy on
              the public page so partial translations never leave the
              visitor with blanks. */}
          <Card title="Spanish Translation (Español)">
            <p className="text-xs text-slate-500 -mt-2 mb-1">
              Fill in the Spanish version of the hero. Visitors who pick
              &ldquo;Español&rdquo; on the language overlay will see these instead
              of the English copy. Leave any field empty to fall back to
              the English version.
            </p>
            <Field label="Titular principal (ES)">
              <Input value={page.content.headline_es || ''}
                onChange={(e) => setPage({ ...page, content: { ...page.content, headline_es: e.target.value } })}
                placeholder="Encontremos la cobertura que se ajusta a tus necesidades"
                data-testid="editor-headline-es" />
            </Field>
            <Field label="Texto de apoyo (ES)">
              <Textarea rows={3} value={page.content.supporting_text_es || ''}
                onChange={(e) => setPage({ ...page, content: { ...page.content, supporting_text_es: e.target.value } })}
                placeholder="¿Tienes preguntas sobre Medicare, seguro médico, seguro de vida u otra cobertura? Completa el formulario y te contactaré personalmente."
                data-testid="editor-supporting-text-es" />
            </Field>
            {page.content.show_highlight && (
              <Field label="Mensaje destacado / tagline (ES)">
                <Input value={page.content.highlight_message_es || ''}
                  onChange={(e) => setPage({ ...page, content: { ...page.content, highlight_message_es: e.target.value } })}
                  placeholder="Tu Protección es Mi Prioridad"
                  data-testid="editor-highlight-message-es" />
              </Field>
            )}
            <Field label="Texto del botón (ES)">
              <Input value={page.content.cta_text_es || ''}
                onChange={(e) => setPage({ ...page, content: { ...page.content, cta_text_es: e.target.value } })}
                placeholder="Solicitar Información"
                data-testid="editor-cta-text-es" />
            </Field>
          </Card>
          {L === 'event' && (
          <Card title="Event / Seminar Details">
            <p className="text-xs text-slate-500 -mt-2 mb-1">Shown as a banner above the RSVP form on the public page.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Date">
                <Input value={page.content.event_date || ''}
                  onChange={(e) => setPage({ ...page, content: { ...page.content, event_date: e.target.value } })}
                  placeholder="Saturday, June 14, 2026"
                  data-testid="editor-event-date" />
              </Field>
              <Field label="Time">
                <Input value={page.content.event_time || ''}
                  onChange={(e) => setPage({ ...page, content: { ...page.content, event_time: e.target.value } })}
                  placeholder="10:00 AM – 12:00 PM"
                  data-testid="editor-event-time" />
              </Field>
              <Field label="Location name">
                <Input value={page.content.event_location || ''}
                  onChange={(e) => setPage({ ...page, content: { ...page.content, event_location: e.target.value } })}
                  placeholder="Tampa Community Center"
                  data-testid="editor-event-location" />
              </Field>
              <Field label="Address">
                <Input value={page.content.event_address || ''}
                  onChange={(e) => setPage({ ...page, content: { ...page.content, event_address: e.target.value } })}
                  placeholder="123 Main St, Tampa, FL"
                  data-testid="editor-event-address" />
              </Field>
            </div>
          </Card>
          )}
          </>)}

          {sKey === 'services' && (
          <Card title="Insurance Interest Options">
            <p className="text-xs text-slate-500 mb-2">Check the services this landing page will offer.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SERVICE_CATALOG.map((s) => (
                <label key={s.key} className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer bg-white hover:border-slate-400"
                  data-testid={`editor-service-${s.key}`}>
                  <input type="checkbox" checked={page.services_enabled.includes(s.key)} onChange={() => toggleService(s.key)} />
                  <span className="text-sm">{s.label}</span>
                </label>
              ))}
            </div>
          </Card>
          )}

          {sKey === 'form' && (
          <Card title="Contact Form Fields">
            <p className="text-xs text-slate-500 mb-2">First name, last name and phone are always required.</p>
            <div className="space-y-3">
              {[
                { key: 'email', label: 'Email' },
                { key: 'zip', label: 'ZIP code' },
                { key: 'preferred_contact', label: 'Preferred contact method' },
                { key: 'message', label: 'Message' },
              ].map((f) => (
                <div key={f.key} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-700">{f.label}</span>
                  <Select value={page.form_config[f.key]}
                    onValueChange={(v) => setPage({ ...page, form_config: { ...page.form_config, [f.key]: v } })}>
                    <SelectTrigger className="w-40" data-testid={`editor-form-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_VISIBILITY_OPTIONS.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </Card>
          )}

          {sKey === 'appointments' && (
          <Card title="Appointments (Online Booking)">
            <p className="text-xs text-slate-500 mb-3">
              Let visitors pick a time directly from the landing page. Bookings become leads, appear
              on the agent&apos;s calendar, sync to Google Calendar (if connected), and trigger a
              notification + confirmation email with an <code>.ics</code> attachment.
            </p>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={!!page.appointments_config?.enabled}
                onChange={(e) => setPage({
                  ...page,
                  appointments_config: {
                    ...(page.appointments_config || EMPTY.appointments_config),
                    enabled: e.target.checked,
                  },
                })}
                data-testid="editor-appt-enabled"
              />
              <span className="font-medium">Enable appointment booking on this landing page</span>
            </label>

            {page.appointments_config?.enabled && (
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Appointment length</div>
                    <Select
                      value={String(page.appointments_config.duration_minutes)}
                      onValueChange={(v) => setPage({
                        ...page,
                        appointments_config: { ...page.appointments_config, duration_minutes: parseInt(v, 10) },
                      })}
                    >
                      <SelectTrigger data-testid="editor-appt-duration"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Minimum notice (hours ahead)</div>
                    <Input
                      type="number" min={0} max={168}
                      value={page.appointments_config.min_notice_hours}
                      onChange={(e) => setPage({
                        ...page,
                        appointments_config: {
                          ...page.appointments_config,
                          min_notice_hours: parseInt(e.target.value || '0', 10),
                        },
                      })}
                      data-testid="editor-appt-min-notice"
                    />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Max days ahead</div>
                    <Input
                      type="number" min={1} max={180}
                      value={page.appointments_config.max_days_ahead}
                      onChange={(e) => setPage({
                        ...page,
                        appointments_config: {
                          ...page.appointments_config,
                          max_days_ahead: parseInt(e.target.value || '30', 10),
                        },
                      })}
                      data-testid="editor-appt-max-days"
                    />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Buffer between appointments (min)</div>
                    <Input
                      type="number" min={0} max={120}
                      value={page.appointments_config.buffer_minutes}
                      onChange={(e) => setPage({
                        ...page,
                        appointments_config: {
                          ...page.appointments_config,
                          buffer_minutes: parseInt(e.target.value || '0', 10),
                        },
                      })}
                      data-testid="editor-appt-buffer"
                    />
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-2">Weekly availability</div>
                  <div className="space-y-2">
                    {[
                      { dow: 0, label: 'Mon' }, { dow: 1, label: 'Tue' }, { dow: 2, label: 'Wed' },
                      { dow: 3, label: 'Thu' }, { dow: 4, label: 'Fri' }, { dow: 5, label: 'Sat' },
                      { dow: 6, label: 'Sun' },
                    ].map((d) => {
                      const wins = (page.appointments_config.available_windows || []).filter((w) => w.dow === d.dow);
                      const has = wins.length > 0;
                      const w = wins[0] || { dow: d.dow, start_time: '09:00', end_time: '17:00' };
                      const upsert = (patch) => {
                        const others = (page.appointments_config.available_windows || []).filter((x) => x.dow !== d.dow);
                        setPage({
                          ...page,
                          appointments_config: {
                            ...page.appointments_config,
                            available_windows: [...others, { ...w, ...patch }],
                          },
                        });
                      };
                      const removeDay = () => {
                        const others = (page.appointments_config.available_windows || []).filter((x) => x.dow !== d.dow);
                        setPage({
                          ...page,
                          appointments_config: { ...page.appointments_config, available_windows: others },
                        });
                      };
                      const addDay = () => upsert({});
                      return (
                        <div key={d.dow} className="flex items-center gap-2" data-testid={`editor-appt-day-${d.dow}`}>
                          <label className="flex items-center gap-2 w-16 text-sm">
                            <input
                              type="checkbox"
                              checked={has}
                              onChange={(e) => (e.target.checked ? addDay() : removeDay())}
                            />
                            {d.label}
                          </label>
                          {has ? (
                            <>
                              <Input type="time" value={w.start_time} className="w-28"
                                onChange={(e) => upsert({ start_time: e.target.value })} />
                              <span className="text-xs text-slate-400">to</span>
                              <Input type="time" value={w.end_time} className="w-28"
                                onChange={(e) => upsert({ end_time: e.target.value })} />
                            </>
                          ) : (
                            <span className="text-xs italic text-slate-400">unavailable</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="text-xs text-slate-500 border-t border-slate-200 pt-3">
                  Timezone: <code>{page.appointments_config.timezone}</code>. All slot times shown to
                  visitors are computed in this timezone.
                </div>
              </div>
            )}
          </Card>
          )}

          {sKey === 'review' && (<>
          {!isNew && (
            <Card title="Status">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Current:</span>
                <span className="font-medium">{page.status}</span>
                <div className="flex-1" />
                {page.status !== 'active' ? (
                  <Button onClick={publish} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Rocket className="h-4 w-4 mr-1" /> Publish
                  </Button>
                ) : (
                  <Button variant="outline" onClick={async () => { await persist({ status: 'inactive' }); }}>
                    Deactivate
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Reassignment prompt — only visible when the assigned
              agent has changed since page load. By default, changing
              the owner only affects FUTURE submissions; this button
              lets the admin explicitly move existing leads too. */}
          {agentChanged && !mine && (
            <Card title="Reassign historical leads?">
              <p className="text-sm text-slate-600">
                You changed the assigned agent. New submissions already go to <b>{selectedAgent ? [selectedAgent.first_name, selectedAgent.last_name].filter(Boolean).join(' ') : page.assigned_agent_npn}</b>.
                Previously-generated leads still belong to the prior owner unless you move them below.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={reassignHistorical} disabled={reassigning}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                  data-testid="editor-reassign-historical">
                  {reassigning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Move all past leads to the new agent
                </Button>
                <Button variant="ghost" onClick={() => setInitialAgentNpn(page.assigned_agent_npn)}>
                  Keep past leads with previous owner
                </Button>
              </div>
            </Card>
          )}
          </>)}

          {/* Wizard navigation */}
          <div className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-3" data-testid="wizard-nav">
            <Button variant="outline" onClick={() => go(sIdx - 1)} disabled={sIdx === 0} data-testid="wizard-back">
              Back
            </Button>
            <span className="text-xs text-slate-500">Step {sIdx + 1} of {stepDefs.length} · {stepDefs[sIdx]?.label}</span>
            {sIdx < stepDefs.length - 1 ? (
              <Button onClick={() => go(sIdx + 1)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="wizard-next">
                Next
              </Button>
            ) : (
              <Button onClick={() => persist()} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wizard-finish">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {isNew ? 'Save draft' : 'Save'}
              </Button>
            )}
          </div>
        </div>
    );
  }

  function previewColumn() {
    return (
        <div className="xl:col-span-1" key="preview-col">
          <div className="sticky top-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Live Preview</h3>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
                <button
                  onClick={() => setPreviewMode('mobile')}
                  className={`px-2 py-1 rounded text-xs ${previewMode === 'mobile' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
                  data-testid="preview-mobile"
                ><Smartphone size={14} /></button>
                <button
                  onClick={() => setPreviewMode('desktop')}
                  className={`px-2 py-1 rounded text-xs ${previewMode === 'desktop' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
                  data-testid="preview-desktop"
                ><Monitor size={14} /></button>
              </div>
            </div>
            <div className={previewMode === 'mobile' ? 'max-w-[280px] mx-auto' : 'w-full'}>
              <InlinePreview page={page} agentProfilePhotoUrl={selectedAgentPhoto} />
            </div>
            {selectedAgent && (
              <p className="mt-3 text-xs text-slate-500 text-center">
                Leads will be assigned to <b>{[selectedAgent.first_name, selectedAgent.last_name].filter(Boolean).join(' ')}</b>
              </p>
            )}
          </div>
        </div>
    );
  }
}

function LeadsTab({ loading, leads }) {
  const navigate = useNavigate();
  if (loading || leads === null) {
    return <div className="text-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }
  if (!leads.length) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 py-12 text-center text-slate-500">
        No leads generated by this page yet.
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
          <tr>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Phone</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Interest</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Assigned Agent</th>
            <th className="text-left px-4 py-3">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-testid="leads-tab-tbody">
          {leads.map((l) => (
            <tr key={l.lead_id}
              className="hover:bg-slate-50 cursor-pointer"
              onClick={() => navigate(`/leads/${l.lead_id}`)}
              data-testid={`leads-row-${l.lead_id}`}>
              <td className="px-4 py-3 font-medium">{l.first_name} {l.last_name}</td>
              <td className="px-4 py-3 text-slate-600">{formatPhone(l.phone) || '—'}</td>
              <td className="px-4 py-3 text-slate-600">{l.email || '—'}</td>
              <td className="px-4 py-3 text-slate-600">{l.product_of_interest || '—'}</td>
              <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs border bg-slate-50">{l.status}</span></td>
              <td className="px-4 py-3 text-slate-600 font-mono text-xs">{l.assigned_agent_npn || '—'}</td>
              <td className="px-4 py-3 text-slate-500">{l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityTab({ loading, rows }) {
  if (loading || rows === null) {
    return <div className="text-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }
  if (!rows.length) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 py-12 text-center text-slate-500">
        No activity recorded yet.
      </div>
    );
  }
  const labelFor = (a) => ({
    created: 'Created',
    edited: 'Edited',
    status_changed: 'Status changed',
    deleted: 'Deleted',
    marketing_photo_uploaded: 'Marketing photo uploaded',
    submission: 'New lead submitted',
    historical_leads_reassigned: 'Historical leads reassigned',
  }[a] || a);
  return (
    <div className="bg-white rounded-2xl border border-slate-200">
      <ul className="divide-y divide-slate-100">
        {rows.map((r) => (
          <li key={r.activity_id} className="px-4 py-3 flex items-start gap-3 text-sm" data-testid={`activity-row-${r.activity_id}`}>
            <div className="w-2 h-2 rounded-full bg-slate-400 mt-2" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900">{labelFor(r.action)}</div>
              <div className="text-xs text-slate-500">
                {r.actor_email || 'System'} · {new Date(r.at).toLocaleString()}
              </div>
              {r.meta && Object.keys(r.meta).length > 0 && (
                <pre className="text-[10px] text-slate-500 mt-1 overflow-x-auto">{JSON.stringify(r.meta, null, 0)}</pre>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AnalyticsTab({ loading, data }) {
  if (loading || data === null) {
    return <div className="text-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }
  const series = (data.series || []).map((d) => ({
    ...d,
    label: new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  }));
  const totals = data.totals || { views: 0, leads: 0 };
  const hasAnyViews = series.some((d) => d.views > 0);
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 gap-4 max-w-md">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-total-views">
          <p className="text-xs uppercase tracking-wider text-slate-500">Total page visits</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{totals.views.toLocaleString()}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-total-leads">
          <p className="text-xs uppercase tracking-wider text-slate-500">Leads received</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{totals.leads.toLocaleString()}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-800">Traffic — last {data.days || 30} days</h3>
        </div>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} interval="preserveStartEnd" minTickGap={20} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="views" name="Visits" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
              <Bar dataKey="leads" name="Leads" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {!hasAnyViews && (
          <p className="text-xs text-slate-400 mt-3">
            Visit tracking starts from today — daily visits will appear here as people open your page.
            The total above reflects all-time visits.
          </p>
        )}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
      <h2 className="font-semibold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

const ROSTER_WEEKDAYS = [
  { dow: 0, label: 'Mon' }, { dow: 1, label: 'Tue' }, { dow: 2, label: 'Wed' },
  { dow: 3, label: 'Thu' }, { dow: 4, label: 'Fri' }, { dow: 5, label: 'Sat' },
  { dow: 6, label: 'Sun' },
];

const ROSTER_DEFAULT_APPT = {
  enabled: true,
  duration_minutes: 30,
  timezone: 'America/New_York',
  available_windows: [
    { dow: 1, start_time: '09:00', end_time: '17:00' },
    { dow: 2, start_time: '09:00', end_time: '17:00' },
    { dow: 3, start_time: '09:00', end_time: '17:00' },
    { dow: 4, start_time: '09:00', end_time: '17:00' },
    { dow: 5, start_time: '09:00', end_time: '17:00' },
  ],
  min_notice_hours: 4,
  max_days_ahead: 30,
  buffer_minutes: 0,
};

function MemberAvailability({ cfg, onChange }) {
  const c = cfg || ROSTER_DEFAULT_APPT;
  return (
    <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[11px] font-semibold text-slate-500 mb-1">Length</div>
          <Select value={String(c.duration_minutes || 30)}
            onValueChange={(v) => onChange({ ...c, duration_minutes: parseInt(v, 10) })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15 min</SelectItem>
              <SelectItem value="30">30 min</SelectItem>
              <SelectItem value="45">45 min</SelectItem>
              <SelectItem value="60">60 min</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-slate-500 mb-1">Max days ahead</div>
          <Input type="number" min={1} max={180} className="h-8 text-xs"
            value={c.max_days_ahead ?? 30}
            onChange={(e) => onChange({ ...c, max_days_ahead: parseInt(e.target.value || '30', 10) })} />
        </div>
      </div>
      <div className="space-y-1">
        {ROSTER_WEEKDAYS.map((d) => {
          const wins = (c.available_windows || []).filter((w) => w.dow === d.dow);
          const has = wins.length > 0;
          const w = wins[0] || { dow: d.dow, start_time: '09:00', end_time: '17:00' };
          const others = (c.available_windows || []).filter((x) => x.dow !== d.dow);
          const upsert = (patch) => onChange({ ...c, available_windows: [...others, { ...w, ...patch }] });
          const removeDay = () => onChange({ ...c, available_windows: others });
          return (
            <div key={d.dow} className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 w-14 text-xs">
                <input type="checkbox" checked={has}
                  onChange={(e) => (e.target.checked ? upsert({}) : removeDay())} />
                {d.label}
              </label>
              {has ? (
                <>
                  <Input type="time" value={w.start_time} className="h-7 w-24 text-xs"
                    onChange={(e) => upsert({ start_time: e.target.value })} />
                  <span className="text-[11px] text-slate-400">to</span>
                  <Input type="time" value={w.end_time} className="h-7 w-24 text-xs"
                    onChange={(e) => upsert({ end_time: e.target.value })} />
                </>
              ) : (
                <span className="text-[11px] italic text-slate-400">unavailable</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamRosterEditor({ agents, roster, onChange, pageId, isNew }) {
  const inRoster = new Set(roster.map((r) => r.npn));
  const available = (agents || []).filter((a) => !inRoster.has(a.npn));
  const add = (npn) => { if (npn) onChange([...roster, { npn, blurb: '', allow_request_info: true, allow_booking: true }]); };
  const remove = (npn) => onChange(roster.filter((r) => r.npn !== npn));
  const setField = (npn, key, value) => onChange(roster.map((r) => (r.npn === npn ? { ...r, [key]: value } : r)));
  const agentOf = (npn) => (agents || []).find((x) => x.npn === npn) || {};
  const nameOf = (npn) => {
    const a = agentOf(npn);
    return [a.first_name, a.last_name].filter(Boolean).join(' ') || npn;
  };
  const initials = (npn) => (nameOf(npn) || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const uploadPhoto = async (npn, file) => {
    if (!file) return;
    if (isNew) { toast.error('Save the page first, then upload agent photos'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await leadLandingPagesAPI.uploadTeamMemberPhoto(pageId, npn, fd);
      setField(npn, 'photo_url', r.data.photo_url);
      toast.success('Agent photo uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };
  const removePhoto = async (npn) => {
    if (!isNew) {
      try { await leadLandingPagesAPI.removeTeamMemberPhoto(pageId, npn); } catch (_) { /* ignore */ }
    }
    setField(npn, 'photo_url', null);
    toast.success('Reverted to profile photo');
  };

  return (
    <div className="space-y-3" data-testid="team-roster-editor">
      <select
        className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
        value=""
        onChange={(e) => add(e.target.value)}
        data-testid="team-roster-add"
      >
        <option value="">+ Add an agent…</option>
        {available.map((a) => (
          <option key={a.npn} value={a.npn}>
            {[a.first_name, a.last_name].filter(Boolean).join(' ')} · NPN {a.npn}
          </option>
        ))}
      </select>
      {roster.length === 0 ? (
        <p className="text-xs text-slate-400">No agents added yet.</p>
      ) : (
        <div className="space-y-3">
          {roster.map((r) => {
            const a = agentOf(r.npn);
            const photo = r.photo_url
              ? (r.photo_url.startsWith('http') ? r.photo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${r.photo_url}`)
              : (a.photo_url || null);
            return (
              <div key={r.npn} className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid={`team-roster-item-${r.npn}`}>
                <div className="flex items-start gap-3">
                  {photo ? (
                    <img src={photo} alt={nameOf(r.npn)} className="h-12 w-12 rounded-full object-cover border border-slate-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-slate-200 grid place-items-center text-sm font-bold text-slate-600">{initials(r.npn)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      {nameOf(r.npn)} <span className="text-xs text-slate-400">NPN {r.npn}</span>
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <label className="text-xs text-teal-600 hover:underline cursor-pointer" data-testid={`team-roster-photo-upload-${r.npn}`}>
                        {r.photo_url ? 'Change photo' : 'Upload photo'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => uploadPhoto(r.npn, e.target.files?.[0])} />
                      </label>
                      {r.photo_url && (
                        <button type="button" onClick={() => removePhoto(r.npn)}
                          className="text-xs text-slate-500 hover:underline" data-testid={`team-roster-photo-remove-${r.npn}`}>
                          Use profile photo
                        </button>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={() => remove(r.npn)}
                    className="text-xs text-rose-600 hover:underline" data-testid={`team-roster-remove-${r.npn}`}>
                    Remove
                  </button>
                </div>

                <Input value={r.blurb || ''} onChange={(e) => setField(r.npn, 'blurb', e.target.value)}
                  placeholder="Habla español · Davenport, FL" className="h-8 text-sm"
                  data-testid={`team-roster-blurb-${r.npn}`} />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={r.email || ''} onChange={(e) => setField(r.npn, 'email', e.target.value)}
                    placeholder={a.email || 'Email (optional)'} className="h-8 text-sm"
                    data-testid={`team-roster-email-${r.npn}`} />
                  <Input value={formatPhoneInput(r.phone || '')} onChange={(e) => setField(r.npn, 'phone', e.target.value)}
                    placeholder={a.phone || 'Phone (optional)'} className="h-8 text-sm"
                    data-testid={`team-roster-phone-${r.npn}`} />
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-1 border-t border-slate-100">
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={r.allow_request_info !== false}
                      onChange={(e) => setField(r.npn, 'allow_request_info', e.target.checked)}
                      data-testid={`team-roster-req-toggle-${r.npn}`} />
                    Show &quot;Request information&quot;
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={r.allow_booking !== false}
                      onChange={(e) => setField(r.npn, 'allow_booking', e.target.checked)}
                      data-testid={`team-roster-book-toggle-${r.npn}`} />
                    Show &quot;Book appointment&quot;
                  </label>
                </div>

                {r.allow_booking !== false && (
                  <div>
                    <div className="text-[11px] font-semibold text-slate-500">Availability for {nameOf(r.npn)}</div>
                    <MemberAvailability
                      cfg={r.appointments_config}
                      onChange={(cfg) => setField(r.npn, 'appointments_config', cfg)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
